/**
 * Why? Sometimes we need custom tooltips, and react-bootstrap OverlayTrigger is unstable
 *  given that it relies on onMouseOver/onMouseLeave event handlers that are not called consistently.
 * Floating UI has been found to work better in practice.
 *
 * Usage (controlled):
 *  <Tooltip.Provider open={open} setOpen={setOpen}>
 *    <Tooltip.Trigger onClick={() => setOpen((v) => !v)}>
 *      ...
 *    </Tooltip.Trigger>
 *    <Tooltip.Content>
 *      ...
 *    </Tooltip.Content>
 *  </Tooltip.Provider>
 *
 */ function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { FloatingArrow, FloatingFocusManager, FloatingPortal, useMergeRefs } from '@floating-ui/react';
import T from 'prop-types';
import React, { createContext, forwardRef, useContext } from 'react';
import useTooltip from '../../hooks/useTooltip';
var TooltipContext = /*#__PURE__*/ createContext(null);
var useTooltipContext = function() {
    var context = useContext(TooltipContext);
    if (!context) {
        throw new Error('not wrapped in TooltipContext.Provider');
    }
    return context;
};
var TooltipProvider = function(_param) {
    var children = _param.children, options = _object_without_properties(_param, [
        "children"
    ]);
    var tooltip = useTooltip(options);
    return /*#__PURE__*/ React.createElement(TooltipContext.Provider, {
        value: tooltip
    }, children);
};
TooltipProvider.propTypes = {
    children: T.node.isRequired
};
var TooltipContent = /*#__PURE__*/ forwardRef(function(_param, propRef) {
    var style = _param.style, _param_withArrow = _param.withArrow, withArrow = _param_withArrow === void 0 ? false : _param_withArrow, _param_arrowProps = _param.arrowProps, arrowProps = _param_arrowProps === void 0 ? {} : _param_arrowProps, props = _object_without_properties(_param, [
        "style",
        "withArrow",
        "arrowProps"
    ]);
    var context = useTooltipContext();
    var ref = useMergeRefs([
        context.refs.setFloating,
        propRef
    ]);
    if (!context.open) return null;
    var _context_getFloatingProps = context.getFloatingProps(props), children = _context_getFloatingProps.children, floatingProps = _object_without_properties(_context_getFloatingProps, [
        "children"
    ]);
    return /*#__PURE__*/ React.createElement(FloatingPortal, null, /*#__PURE__*/ React.createElement(FloatingFocusManager, {
        context: context.context,
        modal: false
    }, /*#__PURE__*/ React.createElement("div", _object_spread({
        ref: ref,
        style: _object_spread_props(_object_spread({}, context.floatingStyles, style), {
            zIndex: 1001
        })
    }, floatingProps), children, withArrow ? /*#__PURE__*/ React.createElement(FloatingArrow, _object_spread({
        ref: context.arrowRef,
        context: context.context
    }, arrowProps)) : null)));
});
TooltipContent.displayName = 'TooltipContent';
TooltipContent.propTypes = {
    rootRef: T.object,
    style: T.object,
    withArrow: T.bool,
    arrowProps: T.object
};
var TooltipTrigger = /*#__PURE__*/ forwardRef(function(_param, propRef) {
    var children = _param.children, props = _object_without_properties(_param, [
        "children"
    ]);
    var context = useTooltipContext();
    var childrenRef = children.ref;
    var ref = useMergeRefs([
        context.refs.setReference,
        propRef,
        childrenRef
    ]);
    return /*#__PURE__*/ React.cloneElement(children, context.getReferenceProps(_object_spread_props(_object_spread({
        ref: ref
    }, props, children.props), {
        'data-state': context.open ? 'open' : 'closed'
    })));
});
TooltipTrigger.displayName = 'TooltipTrigger';
TooltipTrigger.propTypes = {
    children: T.node.isRequired
};
var Tooltip = {
    Provider: TooltipProvider,
    Content: TooltipContent,
    Trigger: TooltipTrigger
};
export default Tooltip;
